// Confirmação de "Desativar": o texto muda com o tipo e a situação, diz o que acontece com a notificação
// da promoção (pendente: é cancelada junto; já enviada: os clientes podem ter recebido) e mostra o motivo
// quando o backend recusa (409 "Desconto em uso na campanha X").
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { __t } from '../../../i18n/t';
import { DiscountTypeKey, getDiscountStatus, typeKey } from './discountStatus';
import { DisableNotification, disableCopy, disableErrorText, disableNotification, disableNotificationCopy, disableToast } from './discountSummary';

interface Props {
    /** O desconto a desativar; null = diálogo fechado. */
    record: any | null;
    /** Nome do cartão (descrição ou "Padrão de ..."). */
    name: string;
    onClose: () => void;
    onDisabled: (toast: string) => void;
    /** Depois da animação de fechar: quem chamou devolve o foco. */
    onExited: () => void;
}

const DisableDiscountDialog = ({ record, name, onClose, onDisabled, onExited }: Props) => {
    const dataProvider = useDataProvider();
    const queryClient = useQueryClient();
    // Durante a animação de fechar o record já é null, mas o texto não pode sumir.
    const last = useRef(record);
    if (record) last.current = record;
    const shown = record ?? last.current;

    // notification: undefined = ainda conferindo; null = sem notificação que importe (nenhuma, cancelada ou com falha).
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState<DisableNotification | null | undefined>(undefined);
    const id = record?.id;

    // Ao abrir, começa do zero (sem erro nem aviso da vez anterior). Ao fechar, mantém o conteúdo
    // até o fim da animação.
    const [wasOpen, setWasOpen] = useState(false);
    if (Boolean(record) !== wasOpen) {
        setWasOpen(Boolean(record));
        if (record) { setBusy(false); setError(''); setNotification(undefined); }
    }

    useEffect(() => {
        if (!id) return;
        // Só promoção tem notificação; os outros tipos não precisam conferir.
        if (typeKey(record) !== DiscountTypeKey.special) { setNotification(null); return; }
        let alive = true;
        // A lista não traz as notificações: busca o desconto inteiro. Se falhar, o backend cancela a
        // pendente do mesmo jeito e o aviso de depois diz que cancelou.
        dataProvider.getOne('discounts', { id })
            .then(({ data }) => { if (alive) setNotification(disableNotification(data)); })
            .catch(() => { if (alive) setNotification(null); });
        return () => { alive = false; };
    }, [id]);

    if (!shown) return null;
    const copy = disableCopy(shown, getDiscountStatus(shown), name);
    const notificationCopy = notification ? disableNotificationCopy(notification) : null;

    const confirm = async () => {
        setBusy(true);
        setError('');
        try {
            // O backend desativa e cancela a notificação pendente na mesma transação.
            const { data } = await dataProvider.update('discounts', { id: shown.id, data: { isEnabled: false }, previousData: { ...shown } });
            // Atualiza a lista e os contadores das abas sem esperar a resposta.
            void queryClient.invalidateQueries({ queryKey: ['discounts'] });
            onDisabled(disableToast(copy.toast, notification ?? null, data?.canceledNotifications));
        } catch (e) {
            setError(disableErrorText(e as { message?: unknown }));
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={Boolean(record)}
            onClose={busy ? undefined : onClose}
            aria-labelledby="disable-discount-title"
            aria-describedby="disable-discount-text"
            // O foco volta por onExited: o botão que abriu pode ter saído da lista quando o diálogo fecha.
            disableRestoreFocus
            slotProps={{ transition: { onExited } }}
            sx={{ '& .MuiDialog-paper': { width: 'calc(100% - 32px)', maxWidth: 480, m: 2, borderRadius: '20px', p: { xs: 0.5, sm: 1 } } }}
        >
            <DialogTitle id="disable-discount-title" sx={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{copy.title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <DialogContentText id="disable-discount-text" sx={{ fontSize: 14.5, lineHeight: 1.5, color: '#475569' }}>{copy.text}</DialogContentText>
                {notificationCopy && (
                    <Alert severity={notificationCopy.severity} role="note">
                        {notificationCopy.text}
                    </Alert>
                )}
                <Alert severity="warning" role="note">
                    Não dá para reativar depois. Para usar de novo, duplique este desconto.
                </Alert>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: 'wrap' }}>
                <Button
                    autoFocus
                    onClick={onClose}
                    disabled={busy}
                    variant="outlined"
                    sx={{ minHeight: 44, px: 2, textTransform: 'none', fontSize: 15, fontWeight: 500, color: '#1e293b', borderColor: '#7c8595', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#7c8595' } }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={confirm}
                    disabled={busy || notification === undefined}
                    variant="contained"
                    disableElevation
                    sx={{ minHeight: 44, px: 2.25, textTransform: 'none', fontSize: 15, fontWeight: 700, bgcolor: '#c62828', color: '#fff', '&:hover': { bgcolor: '#b71c1c' }, '&.Mui-disabled': { bgcolor: 'rgba(198,40,40,.45)', color: '#fff' } }}
                >
                    {busy ? __t('Desativando…') : notificationCopy?.confirm ?? __t('Desativar')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DisableDiscountDialog;
